'use client';

import pathlib from 'path';

import * as ENGINE from '@gnsx/genesys.js';
import { ENGINE_PATH_PREFIX, PROJECT_PATH_PREFIX } from '@gnsx/genesys.js';
import { flow, getParentOfType, type Instance, t, toGenerator } from 'mobx-state-tree';
import { computedFn, createTransformer } from 'mobx-utils';
import { nanoid } from 'nanoid';

import type { IEditorStore } from '../../store/editorStore';
import type { FileItem, FileListResult } from '@gnsx/genesys.js';

let _editorStore: IEditorStore | undefined;
/** Register the editor store singleton for asset-tree helpers. */
export function setAssetTreeEditorStore(editorStore: IEditorStore | null): void {
  _editorStore = editorStore ?? undefined;
}

function getEditorStore(): IEditorStore {
  if (_editorStore) {
    return _editorStore;
  }
  throw new Error('AssetTree editor store has not been initialized yet.');
}

import { JS_CLASSES_DIR_NAME } from '@/const';
import { AssetType, getAssetType, getAssetTypeDisplayName } from '@/editor/core/assetTypes';
import { isExcludedAssetPath } from '@/editor/core/excludedAssetPaths';
import { type IStorageProvider } from '@/lib/storage';
import { IS_DEV } from '@/lib/utils';

const DEBUG_LOG = {
  fetchAssets: true,
  assetsToRemove: IS_DEV,
  refreshDirectory: false,
  updateAssetTreeData: false,
} as const;

export const VIRTUAL_PATH = {
  Root: '@root',
  AllPrefabs: '#all-prefabs',
  AllMaterials: '#all-materials',
  AllTextures: '#all-textures',
  AllModels: '#all-models',
} as const;

export type AssetViewMode = 'grid' | 'list';

export interface IDirectoryTreeNode { id: string; path: string, name: string; children?: IDirectoryTreeNode[]; }
export interface IAssetTypeFilterBadgeState { type: AssetType; enabled: boolean; }

export const DEFAULT_SHOWN_ASSET_TYPE_BADGES: readonly AssetType[] = [
  AssetType.Model,
  AssetType.Prefab,
  AssetType.Material,
  AssetType.Texture,
] as const;

export function createDefaultAssetTypeFilterBadges(): IAssetTypeFilterBadgeState[] {
  return DEFAULT_SHOWN_ASSET_TYPE_BADGES.map(type => ({ type, enabled: false }));
}

export function normalizeAssetTypeFilterBadges(input: readonly IAssetTypeFilterBadgeState[] | null | undefined): IAssetTypeFilterBadgeState[] {
  if (!input || input.length === 0) return createDefaultAssetTypeFilterBadges();

  const validTypes = new Set(Object.values(AssetType));
  const deduped = new Set<AssetType>();
  const normalized: IAssetTypeFilterBadgeState[] = [];

  for (const badge of input) {
    if (!badge || typeof badge !== 'object') continue;
    const type = badge.type;
    if (!validTypes.has(type) || deduped.has(type)) continue;
    deduped.add(type);
    normalized.push({
      type,
      enabled: Boolean(badge.enabled),
    });
  }

  return normalized.length > 0 ? normalized : createDefaultAssetTypeFilterBadges();
}

const AssetTypeFilterBadge = t.model('AssetTypeFilterBadge', {
  type: t.enumeration('AssetType', Object.values(AssetType)),
  enabled: t.optional(t.boolean, false),
});

const getFileUrl = async (path: string): Promise<string> => {
  const storageProvider = getEditorStore().project.makeScopedStorageProvider();
  return (await storageProvider.resolvePath(ENGINE.AssetPath.fromString(path))).getResolvedPath();
};

/** Asset types that support thumbnails - matches ThumbnailService.SUPPORTED_TYPES */
const THUMBNAIL_SUPPORTED_TYPES: Set<AssetType> = new Set([
  AssetType.Model,
  AssetType.Prefab,
  AssetType.Material
]);

/** Helper function to determine if an asset supports thumbnails */
const getSupportsThumbnails = (assetType: AssetType): boolean => {
  return THUMBNAIL_SUPPORTED_TYPES.has(assetType);
};

function matchesAssetSearchTerm(item: IAssetFile, regex: RegExp): boolean {
  const normalizedPath = item.path.replace(/\\/g, '/');
  return regex.test(item.name) || regex.test(normalizedPath);
}

const getSearchRegex = computedFn((searchTerm: string): RegExp | undefined => {
  const trimmed = searchTerm.trim();
  if (!trimmed) {
    return undefined;
  }
  const pattern = trimmed
    .replace(/[-/\\^$+?.()|[\]{}]/g, char => `\\${char}`)
    .replace(/\*/g, '.*');
  return new RegExp(pattern, 'i');
});

/**
 * Helper function to filter resources by their class type
 * This checks if a resource file matches the specified resourceType (class name)
 * Uses the cached resourceClass field from AssetFile
 * @param file - The resource file to check
 * @param resourceType - The resource class name to filter by
 * @returns true if the resource matches the type
 */
function filterResourceByType(file: any, resourceType: string): boolean {
  // If resourceClass is not loaded yet, include the file
  // The resource class will be loaded asynchronously
  if (!file.resourceClass) {
    return true;
  }

  // Match the cached resource class
  // Support both simple class names (e.g., "MyResource") and
  // fully qualified names (e.g., "ENGINE.MyResource" or "GAME.MyResource")
  const fileClass = file.resourceClass;

  // Direct match
  if (fileClass === resourceType) {
    return true;
  }

  // Check if the file class ends with the resource type (handles ENGINE.MyResource vs MyResource)
  if (fileClass.endsWith('.' + resourceType)) {
    return true;
  }

  // Check if the resource type ends with the file class (handles MyResource vs ENGINE.MyResource)
  if (resourceType.endsWith('.' + fileClass)) {
    return true;
  }

  return false;
}

/**
 * Helper function to filter prefabs by their root actor class type.
 * Uses the cached prefabRootClass field from AssetFile.
 * Excludes files whose root class has not yet been loaded.
 * @param file - The prefab file to check
 * @param spawnableClass - The actor class name to filter by (e.g. 'ModelMeshActor')
 * @returns true if the prefab's root class is a subclass of spawnableClass
 */
function filterPrefabByClass(file: any, spawnableClass: string): boolean {
  if (!file.prefabRootClass) {
    return false;
  }

  const rootClass = file.prefabRootClass;

  // spawnableClass is a short name (e.g. 'GameMode') — use the safe lookup
  const baseConstructor = ENGINE.ClassRegistry.getClassByConstructorName(spawnableClass);
  if (!baseConstructor) {
    return false;
  }

  // rootClass is fully-qualified (e.g. 'ENGINE.Actor') — look up directly in the registry map
  const registry = ENGINE.ClassRegistry.getRegistry();
  const rootConstructor = registry.get(rootClass) ?? ENGINE.ClassRegistry.getClassByConstructorName(rootClass);
  if (!rootConstructor) {
    return false;
  }

  // Check prototype chain
  let proto: any = rootConstructor;
  while (proto) {
    if (proto === baseConstructor) return true;
    proto = Object.getPrototypeOf(proto);
  }

  return false;
}

/**
 * AssetFile state of file item.
 */
export const AssetFile = t.model('AssetFile', {
  id: t.optional(t.string, () => nanoid()),
  path: t.identifier,
  name: t.string,
  url: t.optional(t.string, ''),
  absolutePath: t.optional(t.string, ''),
  size: t.optional(t.number, 0),
  modifiedTime: t.optional(t.Date, new Date()),
  contentType: t.maybe(t.string),
  isFolder: t.boolean,
  assetType: t.optional(t.enumeration('AssetType', Object.values(AssetType)), AssetType.Json),
  supportsThumbnails: t.optional(t.boolean, false),
  isExpanded: t.optional(t.boolean, false),
  resourceClass: t.maybe(t.string), // Cached resource class name for .res.json files
  prefabRootClass: t.maybe(t.string), // Cached root actor class name for .prefab.json files
}).volatile(() => ({
  dirty: false, // TODO: only save to file when dirty
  thumbnailUpdateCounter: 0,
  previewUpdateCounter: 0, // Used to trigger preview re-renders without affecting thumbnails
})).views(self => ({
  get isDirectory(): boolean { return self.isFolder; },
  get assetTypeName(): string { return getAssetTypeDisplayName(self.assetType); },
  get normalizedAbsolutePath(): string { return self.absolutePath.replace(/\\/g, '/'); },
  get isProjectItem(): boolean { return self.path.startsWith(PROJECT_PATH_PREFIX); },
  get isEngineAsset(): boolean { return self.path.startsWith(ENGINE_PATH_PREFIX); },
  get isSelected(): boolean {
    const tree = getParentOfType(self, AssetTree);
    return self.isFolder ? tree?.isSelectedFolder(self.path) : tree?.isSelectedFile(self.path);
  },
  get hasControls(): boolean {
    return self.assetType === AssetType.Material || self.assetType === AssetType.Resource;
  },
  get thumbnailPath(): string {
    if (!self.supportsThumbnails) return '';
    const thumbnailService = getEditorStore().thumbnailService;
    return thumbnailService?.getThumbnailPath(self as IAssetFile) ?? '';
  },
  get thumbnailUrl(): string {
    if (!self.supportsThumbnails || !self.url) return '';
    // For now, return empty - will be resolved when thumbnail is generated
    return '';
  },
})).actions(self => ({
  update(fileItem: FileItem): boolean {
    const prevModifiedTime = self.modifiedTime?.getTime?.() ?? 0;
    const nextModifiedTime = fileItem.modifiedTime?.getTime?.() ?? 0;
    const pathChanged = self.path !== fileItem.path;
    const changed = pathChanged
      || self.name !== fileItem.name
      || self.absolutePath !== fileItem.absolutePath
      || self.size !== fileItem.size
      || prevModifiedTime !== nextModifiedTime
      || self.contentType !== fileItem.contentType
      || self.isFolder !== fileItem.isDirectory;

    if (!changed) {
      return false;
    }

    self.path = fileItem.path;
    self.name = fileItem.name;
    self.absolutePath = fileItem.absolutePath;
    self.size = fileItem.size;
    self.modifiedTime = fileItem.modifiedTime;
    self.contentType = fileItem.contentType;
    self.isFolder = fileItem.isDirectory;
    self.assetType = getAssetType(self as IAssetFile);
    self.supportsThumbnails = getSupportsThumbnails(self.assetType);
    if (pathChanged || !self.url) {
      this.resolveUrl();
    }
    return true;
  },
  setDirty(
    dirty: boolean = true,
    options?: { updateThumbnail?: boolean; updatePreview?: boolean }
  ) {
    self.dirty = dirty;
    if (dirty) {
      const shouldUpdateThumbnail = options?.updateThumbnail ?? true;
      const shouldUpdatePreview = options?.updatePreview ?? true;
      // Don't modify modifiedTime - it should only reflect actual file modification time
      // Instead, increment update counters to trigger preview/thumbnail refresh.
      if (shouldUpdateThumbnail) {
        self.thumbnailUpdateCounter++;
      }
      if (shouldUpdatePreview) {
        self.previewUpdateCounter++;
      }
    }
  },
  refreshThumbnail() {
    self.thumbnailUpdateCounter++;
  },
  refreshPreview() {
    self.previewUpdateCounter++;
  },
  setExpanded(expanded: boolean) {
    if (self.isFolder) {
      self.isExpanded = expanded;
    }
  },
  toggleExpanded() {
    if (self.isFolder) {
      self.isExpanded = !self.isExpanded;
    }
  },
  resolveUrl: flow(function* _updateUrl() {
    try {
      const url = yield getFileUrl(self.path);
      self.url = url;
    } catch (error) {
      console.warn('Error refreshing file url:', error);
    }
  }),
  /** Load and cache resource class for .res.json files */
  loadResourceClass: flow(function* _loadResourceClass(): Generator<Promise<string>, void, string> {
    // Only load for resource files
    if (self.assetType !== AssetType.Resource) {
      return;
    }

    try {
      const storageProvider: IStorageProvider = getEditorStore().project.makeScopedStorageProvider();
      const assetPath = ENGINE.AssetPath.fromString(self.path);
      const content: string = yield storageProvider.downloadFileAsText(assetPath);

      if (content) {
        const data = ENGINE.parseJson(content) as ENGINE.SerializedData;
        const rootClass = ENGINE.WorldSerializer.readRootClassFromSerializedData(data);
        if (typeof rootClass === 'string') {
          self.resourceClass = rootClass;
        }
      }
    } catch (error) {
      // Silently fail - resource class will remain undefined
      // This is acceptable as filterResourceByType will handle undefined gracefully
    }
  }),
  /** Load and cache the root actor class for .prefab.json files */
  loadPrefabRootClass: flow(function* _loadPrefabRootClass(): Generator<Promise<string>, void, string> {
    // Only load for prefab files
    if (self.assetType !== AssetType.Prefab) {
      return;
    }

    try {
      const storageProvider: IStorageProvider = getEditorStore().project.makeScopedStorageProvider();
      const assetPath = ENGINE.AssetPath.fromString(self.path);
      const content: string = yield storageProvider.downloadFileAsText(assetPath);

      if (content) {
        const data = ENGINE.parseJson(content) as ENGINE.SerializedData;
        const rootClass = ENGINE.WorldSerializer.readRootClassFromSerializedData(data);
        if (typeof rootClass === 'string') {
          self.prefabRootClass = rootClass;
        }
      }
    } catch (error) {
      // Silently fail — prefabRootClass will remain undefined and the file will be excluded by filterPrefabByClass
    }
  }),
}));

/**
 * AssetTree state of folders and files.
 */
export const AssetTree = t.model('AssetTree', {
  customFolders: t.map(AssetFile), // path -> folder
  folders: t.map(AssetFile), // path -> folder
  files: t.map(AssetFile), // path -> file
  currentFolder: t.safeReference(AssetFile),
  currentFile: t.safeReference(AssetFile),
  selectedItem: t.safeReference(AssetFile), // Can be a folder or a file
  highlightedItemPath: t.optional(t.string, ''),
  preSelectedPath: t.optional(t.string, ''), // Path of item we expect to be available soon
  searchTerm: t.optional(t.string, ''),
  folderSearchTerm: t.optional(t.string, ''), // Search term for filtering folder paths
  assetTypeFilterBadges: t.optional(
    t.array(AssetTypeFilterBadge),
    () => createDefaultAssetTypeFilterBadges(),
  ),
  viewMode: t.optional(t.enumeration('ViewMode', ['grid', 'list']), 'grid'),
  showHiddenFiles: t.optional(t.boolean, false),
}).views(self => ({
  isSelectedFolder(path: string): boolean { return self.currentFolder?.path === path; },
  isSelectedFile(path: string): boolean { return self.currentFile?.path === path; },
  findFolder(path: string): IAssetFile | undefined { return self.folders.get(path) ?? self.customFolders.get(path); },
  findFile(path: string): IAssetFile | undefined { return self.files.get(path); },
  get shownAssetTypeBadges(): ReadonlyArray<IAssetTypeFilterBadgeState> {
    return self.assetTypeFilterBadges.map(badge => ({ type: badge.type, enabled: badge.enabled }));
  },
  get selectedAssetTypes(): AssetType[] {
    return self.assetTypeFilterBadges.filter(badge => badge.enabled).map(badge => badge.type);
  },
  get directoryTree(): IDirectoryTreeNode[] { return directoryTreeTransformer(self as IAssetTree); },
  get currentFolderPath(): string { return self.currentFolder?.path ?? ''; },
  get currentFilePath(): string { return self.currentFile?.path ?? ''; },
  get currentDirectoryItemsRaw(): IAssetFile[] {
    const tree = self as IAssetTree;
    const selectedAssetTypes = this.selectedAssetTypes;
    const hasAssetTypeFilter = selectedAssetTypes.length > 0;
    const hasSearchFilter = self.searchTerm.trim().length > 0;
    const isRootDirectory = this.currentFolderPath === '' || this.currentFolderPath === VIRTUAL_PATH.Root;

    if (isRootDirectory) {
      return getAllCachedItems(tree);
    }

    const includeDescendants = hasSearchFilter || hasAssetTypeFilter;
    return getCachedFilesIn(tree, this.currentFolderPath, includeDescendants);
  },
  get currentDirectoryItemsFiltered(): IAssetFile[] {
    const selectedAssetTypes = this.selectedAssetTypes;
    const hasAssetTypeFilter = selectedAssetTypes.length > 0;
    let items = this.currentDirectoryItemsRaw;

    const searchRegex = getSearchRegex(self.searchTerm);
    if (searchRegex) {
      items = items.filter(item => matchesAssetSearchTerm(item, searchRegex));
    }

    if (hasAssetTypeFilter) {
      const selectedAssetTypeSet = new Set(selectedAssetTypes);
      items = items.filter(item => selectedAssetTypeSet.has(item.assetType));
    }

    return items;
  },
  get currentItems(): IAssetFile[] {
    return this.currentDirectoryItemsFiltered;
  },
  getItemsIn(path: string, recursive: boolean, filter?: (item: IAssetFile) => boolean): IAssetFile[] {
    return getCachedFilesIn(self as IAssetTree, path, recursive, filter);
  },
  getItems(filter?: (item: IAssetFile) => boolean): IAssetFile[] {
    return getCachedItems(self as IAssetTree, filter);
  },
  get assetSelectCacheVersion(): number {
    let hash = self.files.size;
    for (const file of self.files.values()) {
      hash = (hash * 33) ^ file.path.length;
      hash = (hash * 33) ^ file.assetType.length;
      hash = (hash * 33) ^ (file.resourceClass?.length ?? 0);
      hash = (hash * 33) ^ (file.prefabRootClass?.length ?? 0);
    }
    return hash >>> 0;
  },
  getAssetFiles(assetTypes: AssetType | AssetType[], resourceType?: string, prefabClass?: string): IAssetFile[] {
    const typeToGetter: Partial<Record<AssetType, () => IAssetFile[]>> = {
      [AssetType.Texture]: () => this.textureFiles,
      [AssetType.HDRI]: () => this.hdriFiles,
      [AssetType.Material]: () => this.materialFiles,
      [AssetType.Model]: () => this.modelFiles,
      [AssetType.Prefab]: () => this.prefabFiles,
      [AssetType.Scene]: () => this.sceneFiles,
      [AssetType.SourceCode]: () => this.sourceCodeFiles,
      [AssetType.JsClass]: () => this.jsClassFiles,
      [AssetType.Resource]: () => this.resourceFiles,
      [AssetType.VFX]: () => this.vfxFiles,
    };
    const isArray = Array.isArray(assetTypes);
    let files: IAssetFile[];

    if (!isArray || assetTypes.length === 1) {
      const type = isArray ? assetTypes[0] : assetTypes;
      const getter = typeToGetter[type];
      files = getter ? getter() : Array.from(self.files.values()).filter(file => file.assetType === type);
    } else {
      files = assetTypes.flatMap(type => {
        const getter = typeToGetter[type];
        return getter ? getter() : Array.from(self.files.values()).filter(file => file.assetType === type);
      });
    }

    // Filter by resourceType if specified (only applies to Resource assets)
    if (resourceType && files.some(f => f.assetType === AssetType.Resource)) {
      files = files.filter(file => {
        if (file.assetType !== AssetType.Resource) return true;
        return filterResourceByType(file, resourceType);
      });
    }

    // Filter by prefabClass if specified (only applies to Prefab assets)
    if (prefabClass && files.some(f => f.assetType === AssetType.Prefab)) {
      files = files.filter(file => {
        if (file.assetType !== AssetType.Prefab) return true;
        return filterPrefabByClass(file, prefabClass);
      });
    }

    return files;
  },
  get allFiles(): IAssetFile[] { return Array.from(self.files.values()); },
  get textureFiles(): IAssetFile[] { return Array.from(self.files.values()).filter(file => file.assetType === AssetType.Texture); },
  get hdriFiles(): IAssetFile[] { return Array.from(self.files.values()).filter(file => file.assetType === AssetType.HDRI); },
  get materialFiles(): IAssetFile[] { return Array.from(self.files.values()).filter(file => file.assetType === AssetType.Material); },
  get modelFiles(): IAssetFile[] { return Array.from(self.files.values()).filter(file => file.assetType === AssetType.Model); },
  get prefabFiles(): IAssetFile[] { return Array.from(self.files.values()).filter(file => file.assetType === AssetType.Prefab); },
  get sceneFiles(): IAssetFile[] { return Array.from(self.files.values()).filter(file => file.assetType === AssetType.Scene); },
  get resourceFiles(): IAssetFile[] { return Array.from(self.files.values()).filter(file => file.assetType === AssetType.Resource); },
  get vfxFiles(): IAssetFile[] { return Array.from(self.files.values()).filter(file => file.assetType === AssetType.VFX); },
  get sourceCodeFiles(): IAssetFile[] { return Array.from(self.files.values()).filter(file => file.assetType === AssetType.SourceCode); },
  get jsClassFiles(): IAssetFile[] { return Array.from(self.files.values()).filter(file => file.assetType === AssetType.JsClass); },
  get expandedFolders(): string[] {
    return [...self.customFolders.values(), ...self.folders.values()]
      .filter(folder => folder.isExpanded)
      .map(folder => folder.path);
  },
  isFolderExpanded(path: string): boolean {
    const folder = this.findFolder(path);
    return folder?.isExpanded ?? false;
  },
})).actions(self => ({
  setViewMode(viewMode: AssetViewMode) {
    self.viewMode = viewMode;
  },
  setSearchTerm(searchTerm: string) {
    self.searchTerm = searchTerm;
  },
  setFolderSearchTerm(folderSearchTerm: string) {
    self.folderSearchTerm = folderSearchTerm;
  },
  setAssetTypeFilterBadges(badges: IAssetTypeFilterBadgeState[]) {
    const normalized = normalizeAssetTypeFilterBadges(badges);
    self.assetTypeFilterBadges.replace(normalized);
  },
  toggleAssetTypeFilterBadge(type: AssetType) {
    const existingBadge = self.assetTypeFilterBadges.find(badge => badge.type === type);
    if (existingBadge) {
      existingBadge.enabled = !existingBadge.enabled;
      return;
    }
    self.assetTypeFilterBadges.push({ type, enabled: true });
  },
  removeAssetTypeFilterBadge(type: AssetType) {
    const index = self.assetTypeFilterBadges.findIndex(badge => badge.type === type);
    if (index >= 0) {
      self.assetTypeFilterBadges.splice(index, 1);
    }
  },
  setSelectedAssetTypes(types: AssetType[]) {
    const selectedTypeSet = new Set(types);
    const currentBadges = self.assetTypeFilterBadges.map(badge => ({
      type: badge.type,
      enabled: selectedTypeSet.has(badge.type),
    }));

    for (const type of types) {
      if (!currentBadges.some(badge => badge.type === type)) {
        currentBadges.push({ type, enabled: true });
      }
    }

    this.setAssetTypeFilterBadges(currentBadges);
  },
  setPreSelectedItem(path: string) {
    self.preSelectedPath = path ?? '';
  },
  setHighlightedItemPath(path: string | null | undefined) {
    const nextPath = path?.trim() ?? '';
    self.highlightedItemPath = nextPath.startsWith('/@') ? nextPath.slice(1) : nextPath;
  },
  clearHighlightedItemPath() {
    self.highlightedItemPath = '';
  },
  setSelectedItem(asset: IAssetFile | undefined) {
    const isSameSelection = self.selectedItem === asset;
    self.selectedItem = asset;
    self.preSelectedPath = ''; // Clear preselected path
    if (asset?.path && self.highlightedItemPath && asset.path !== self.highlightedItemPath) {
      self.highlightedItemPath = '';
    }
    if (isSameSelection) {
      return;
    }
    if (asset) {
      !asset?.isFolder && this.setCurrentFile(asset.path);
      asset.resolveUrl();
      // Re-check thumbnail vs storage on selection without forcing UI refresh/flicker.
      if (!asset.isFolder && asset.supportsThumbnails) {
        const thumbnailService = getEditorStore().thumbnailService;
        if (thumbnailService) {
          thumbnailService.queueThumbnailGeneration(asset, 20);
        }
      }
    }
  },
  setCurrentFolder(path: string | undefined) {
    self.currentFolder = path ? self.folders.get(path) ?? self.customFolders.get(path) : undefined;
  },
  setCurrentFile(path: string | undefined) {
    self.currentFile = path ? self.files.get(path) : undefined;
  },
  /** Update asset file path in the map (for rename operations) */
  updateAssetFilePath(oldPath: string, newPath: string, newName: string) {
    const oldFile = self.files.get(oldPath);
    if (oldFile) {
      const shouldUpdateCurrentFile = self.currentFile?.path === oldPath;
      const shouldUpdateSelectedItem = self.selectedItem?.path === oldPath;
      const shouldUpdateHighlightedPath = self.highlightedItemPath === oldPath;

      // Since path is an identifier, we need to create a new instance
      // Create a snapshot of the old file
      const snapshot = {
        id: oldFile.id,
        path: newPath,
        name: newName,
        url: oldFile.url,
        absolutePath: oldFile.absolutePath.replace(oldFile.name, newName),
        size: oldFile.size,
        modifiedTime: oldFile.modifiedTime,
        contentType: oldFile.contentType,
        isFolder: oldFile.isFolder,
        assetType: oldFile.assetType,
        supportsThumbnails: oldFile.supportsThumbnails,
        isExpanded: oldFile.isExpanded,
        resourceClass: oldFile.resourceClass,
        prefabRootClass: oldFile.prefabRootClass,
      };

      // Remove old and add new
      self.files.delete(oldPath);
      const newFile = AssetFile.create(snapshot);
      self.files.set(newPath, newFile);

      // Update current file reference if it was selected
      if (shouldUpdateCurrentFile) {
        self.currentFile = newFile;
      }
      if (shouldUpdateSelectedItem) {
        self.selectedItem = newFile;
      }
      if (shouldUpdateHighlightedPath) {
        self.highlightedItemPath = newPath;
      }
    }
  },
  setExpandedFolders(updater: string[] | ((ids: string[]) => string[])) {
    const tree = self as IAssetTree;
    const currentExpanded = tree.expandedFolders;
    const newExpandedIds = typeof updater === 'function' ? updater(currentExpanded) : updater;

    // Get current expanded folders
    const currentExpandedSet = new Set(currentExpanded);
    const newExpandedSet = new Set(newExpandedIds);

    // Find folders that were collapsed
    for (const path of currentExpandedSet) {
      if (!newExpandedSet.has(path)) {
        const folder = self.folders.get(path) ?? self.customFolders.get(path);
        if (folder) {
          folder.setExpanded(false);
        }
      }
    }

    // Find folders that were expanded
    for (const path of newExpandedSet) {
      if (!currentExpandedSet.has(path)) {
        const folder = self.folders.get(path) ?? self.customFolders.get(path);
        if (folder) {
          folder.setExpanded(true);
        }
      }
    }
  },
  refreshCurrentDirectory: flow(function* _refreshCurrentDirectory(recursive: boolean = false) {
    try {
      const results = yield* toGenerator(fetchAssets(self.currentFolderPath, recursive));
      updateAssetTreeData(self as IAssetTree, results.directories, results.files, recursive, self.currentFolderPath);
      getEditorStore().scene.bumpMissingAssetDiagnosticsEpoch();
    } catch (error) {
      console.warn('Error refreshing current directory:', error);
    }
  }),
  refreshDirectory: flow(function* _refreshCurrentDirectory(path: string, recursive: boolean = false) {
    try {
      if (DEBUG_LOG.refreshDirectory) console.log('🔄 refreshDirectory:', { path, recursive });
      const results = yield* toGenerator(fetchAssets(path, recursive));
      if (DEBUG_LOG.refreshDirectory) console.log('🔄 refreshDirectory fetched:', {
        path,
        directories: results.directories.length,
        files: results.files.length,
        directoriesDetail: results.directories.map(d => d.path),
        filesDetail: results.files.map(f => f.path)
      });
      updateAssetTreeData(self as IAssetTree, results.directories, results.files, recursive, path);
      getEditorStore().scene.bumpMissingAssetDiagnosticsEpoch();
    } catch (error) {
      console.warn('Error refreshing directory:', error);
    }
  }),
  refreshProjectDirectories: flow(function* _refreshProjectDirectories(recursive: boolean = true) {
    try {
      const results: PromiseSettledResult<FileListResult>[] = yield* toGenerator(Promise.allSettled([
        fetchAssets(PROJECT_PATH_PREFIX, recursive),
        fetchAssets(pathlib.join(PROJECT_PATH_PREFIX, JS_CLASSES_DIR_NAME), recursive),
      ]));
      const fulfilled = results.filter((r): r is PromiseFulfilledResult<FileListResult> => r.status === 'fulfilled');
      const allDirectories = fulfilled.flatMap(r => r.value.directories);
      const allFiles = fulfilled.flatMap(r => r.value.files);
      updateAssetTreeData(self as IAssetTree, allDirectories, allFiles, recursive, PROJECT_PATH_PREFIX);
      getEditorStore().scene.bumpMissingAssetDiagnosticsEpoch();
    } catch (error) {
      console.warn('Error refreshing project directories:', error);
    }
  }),
  refreshAllDirectories: flow(function* _initRefreshAllDirectories(recursive: boolean = true, initial: boolean = false) {
    try {
      const settled: PromiseSettledResult<FileListResult>[] = yield* toGenerator(Promise.allSettled([
        fetchAssets(PROJECT_PATH_PREFIX, recursive),
        fetchAssets(ENGINE_PATH_PREFIX, recursive),
        fetchAssets(pathlib.join(PROJECT_PATH_PREFIX, JS_CLASSES_DIR_NAME), recursive),
        fetchAssets(pathlib.join(ENGINE_PATH_PREFIX, JS_CLASSES_DIR_NAME), recursive)
      ]));
      const operations = ['Project', 'Engine', 'JS Class Project', 'JS Class Engine'];
      settled.forEach((result, index) => {
        if (result.status === 'rejected') {
          console.error(`Error fetching ${operations[index]} assets:`, result.reason);
        }
      });
      const fulfilled = settled.filter((r): r is PromiseFulfilledResult<FileListResult> => r.status === 'fulfilled');
      const allDirectories = fulfilled.flatMap(r => r.value.directories);
      const allFiles = fulfilled.flatMap(r => r.value.files);
      updateAssetTreeData(self as IAssetTree, allDirectories, allFiles, recursive, undefined, initial);
      getEditorStore().scene.bumpMissingAssetDiagnosticsEpoch();

    } catch (error) {
      console.warn('Error refreshing all directories:', error);
    }
  }),
  resetThumbnailSession() {
    const thumbnailService = getEditorStore().thumbnailService;
    if (thumbnailService) {
      thumbnailService.resetSession();

      // Trigger thumbnail generation for current directory after session reset
      const currentAssets = self.currentItems;
      if (currentAssets.length > 0) {
        console.log(`🖼️ Triggering thumbnail generation for ${currentAssets.length} assets in current directory after project load`);
        setTimeout(() => {
          currentAssets.forEach(asset => {
            if (thumbnailService.isSupported(asset.assetType)) {
              thumbnailService.queueThumbnailGeneration(asset, 8); // High priority for initial load
            }
          });
        }, 1000); // 1 second delay to let project fully load
      }
    }
  }
})).actions(self => ({
  /* Lifecycle hooks */
  afterCreate() {
    self.customFolders.put({ path: VIRTUAL_PATH.AllPrefabs, name: 'All Prefabs', isFolder: true });
    self.customFolders.put({ path: VIRTUAL_PATH.AllMaterials, name: 'All Materials', isFolder: true });
    self.customFolders.put({ path: VIRTUAL_PATH.AllTextures, name: 'All Textures', isFolder: true });
    self.customFolders.put({ path: VIRTUAL_PATH.AllModels, name: 'All Models', isFolder: true });
    self.customFolders.put({ path: VIRTUAL_PATH.Root, name: VIRTUAL_PATH.Root, isFolder: true });
    self.customFolders.put({ path: PROJECT_PATH_PREFIX, name: PROJECT_PATH_PREFIX, isFolder: true, isExpanded: true });
    self.customFolders.put({ path: ENGINE_PATH_PREFIX, name: ENGINE_PATH_PREFIX, isFolder: true });

    // Set default expanded state for project assets folder
    const projectAssetsPath = PROJECT_PATH_PREFIX + '/assets';
    // We'll set this when the folder is created during asset fetching
  }
}));

export interface IAssetFile extends Instance<typeof AssetFile> { }
export interface IAssetTree extends Instance<typeof AssetTree> { }

function getCachedFilesIn(assetTree: IAssetTree, path: string, recursive: boolean, filter?: (item: IAssetFile) => boolean): IAssetFile[] {
  if (DEBUG_LOG.updateAssetTreeData) console.log('📂 getCachedFilesIn:', { path, recursive });

  let result: IAssetFile[];
  if (path === VIRTUAL_PATH.Root) {
    result = [assetTree.customFolders.get(PROJECT_PATH_PREFIX)!, assetTree.customFolders.get(ENGINE_PATH_PREFIX)!];
  } else if (path === VIRTUAL_PATH.AllPrefabs) {
    result = getAllCachedItems(assetTree).filter(item => item.assetType === AssetType.Prefab);
  } else if (path === VIRTUAL_PATH.AllMaterials) {
    result = getAllCachedItems(assetTree).filter(item => item.assetType === AssetType.Material);
  } else if (path === VIRTUAL_PATH.AllTextures) {
    result = getAllCachedItems(assetTree).filter(item => item.assetType === AssetType.Texture || item.assetType === AssetType.HDRI);
  } else if (path === VIRTUAL_PATH.AllModels) {
    result = getAllCachedItems(assetTree).filter(item => item.assetType === AssetType.Model);
  } else {
    result = getItemsInPath(assetTree, path, recursive);
  }

  if (filter) {
    result = result.filter(item => filter(item));
  }

  if (DEBUG_LOG.updateAssetTreeData) console.log('📂 getCachedFilesIn result:', { path, recursive, resultCount: result.length });
  return result;
}

const getAllCachedItems = computedFn((assetTree: IAssetTree): IAssetFile[] => [
  ...assetTree.customFolders.values(),
  ...assetTree.folders.values(),
  ...assetTree.files.values()
]);

function getCachedItems(assetTree: IAssetTree, filter?: (item: IAssetFile) => boolean): IAssetFile[] {
  const items = getAllCachedItems(assetTree);
  return filter ? items.filter(filter) : items;
}

const getItemsInPath = computedFn((assetTree: IAssetTree, path: string, recursive: boolean): IAssetFile[] => {
  const allItems = getAllCachedItems(assetTree);
  return allItems.filter(item => {
    const itemInScope = recursive
      ? item.path.startsWith(path)
      : pathlib.dirname(item.path) === path;
    if (DEBUG_LOG.updateAssetTreeData && itemInScope) {
      console.log('📂 Item in scope:', {
        itemPath: item.path,
        scopePath: path,
        recursive,
        itemParentDir: pathlib.dirname(item.path)
      });
    }
    return itemInScope;
  });
});

const EMPTY_FILE_LIST_RESULT: FileListResult = { files: [], directories: [] };

/**
 * Fetches assets - getSrcDirectory.
 */
async function fetchAssets(path: string, recursive: boolean = false): Promise<FileListResult> {
  const store = getEditorStore();
  if (!store.project.isProjectOpened) {
    return EMPTY_FILE_LIST_RESULT;
  }
  const storageProvider = store.project.makeScopedStorageProvider();
  const showHiddenFiles = store.assets.assetTree.showHiddenFiles;

  if (DEBUG_LOG.fetchAssets) console.log('AssetTree.fetchAssets', path, recursive, showHiddenFiles);

  let result: FileListResult;
  if (path === VIRTUAL_PATH.Root) {
    // Manually inject @project and @engine as root folders
    result = {
      files: [], directories: [
        { name: PROJECT_PATH_PREFIX, path: PROJECT_PATH_PREFIX, size: 0, modifiedTime: new Date(), isDirectory: true, absolutePath: PROJECT_PATH_PREFIX },
        { name: ENGINE_PATH_PREFIX, path: ENGINE_PATH_PREFIX, size: 0, modifiedTime: new Date(), isDirectory: true, absolutePath: ENGINE_PATH_PREFIX },
      ]
    };
  } else if (path === VIRTUAL_PATH.AllPrefabs || path === VIRTUAL_PATH.AllModels || path === VIRTUAL_PATH.AllMaterials) {
    // TODO: API should probably support filtering by asset type!
    const [projectResults, engineResults] = await Promise.all([
      storageProvider.listFiles(ENGINE.AssetPath.fromString(PROJECT_PATH_PREFIX), true, false),
      storageProvider.listFiles(ENGINE.AssetPath.fromString(ENGINE_PATH_PREFIX), true, false)
    ]);
    const type = path === VIRTUAL_PATH.AllPrefabs ? AssetType.Prefab : path === VIRTUAL_PATH.AllModels ? AssetType.Model : path === VIRTUAL_PATH.AllMaterials ? AssetType.Material : AssetType.Prefab;
    result = { files: [...projectResults.files, ...engineResults.files].filter(file => getAssetType(file) === type), directories: [] };
  } else if (pathlib.basename(path) === JS_CLASSES_DIR_NAME) {
    // Populate JS Classes using actor classes only by design.
    // Component classes are intentionally excluded from this virtual folder.
    const prefix = path.includes(PROJECT_PATH_PREFIX) ? ENGINE.Prefix.GAME : (path.includes(ENGINE_PATH_PREFIX) ? ENGINE.Prefix.ENGINE : '');
    const actorClasses = store.assets.actorClasses;
    result = {
      files: actorClasses.filter((classInfo) => classInfo.className.startsWith(prefix))
        .map((classInfo) => ({
          name: classInfo.displayName,
          path: pathlib.join(path, classInfo.className),
          absolutePath: pathlib.join(path, classInfo.className),
          size: 0,
          modifiedTime: new Date(),
          isDirectory: false,
        })), directories: []
    };
  } else {
    result = await storageProvider.listFiles(ENGINE.AssetPath.fromString(path), recursive, false);
    const baseName = pathlib.basename(path);
    if (baseName === PROJECT_PATH_PREFIX || baseName === ENGINE_PATH_PREFIX) {
      result.directories.push({
        name: JS_CLASSES_DIR_NAME,
        path: pathlib.join(path, JS_CLASSES_DIR_NAME),
        absolutePath: pathlib.join(path, JS_CLASSES_DIR_NAME),
        size: 0,
        modifiedTime: new Date(),
        isDirectory: true,
      });
    }
  }
  if (!showHiddenFiles) {
    // Filter out files and directories that are hidden or nested within hidden directories
    const isHiddenPath = (path: string) => path.split('/').some(part => part.startsWith('.'));
    result.files = result.files.filter(file => !isHiddenPath(file.path) && !isExcludedAssetPath(file.path));
    result.directories = result.directories.filter(directory => !isHiddenPath(directory.path) && !isExcludedAssetPath(directory.path));
  } else {
    result.files = result.files.filter(file => !isExcludedAssetPath(file.path));
    result.directories = result.directories.filter(directory => !isExcludedAssetPath(directory.path));
  }
  return result;
}

/**
 * Updates the asset tree with new directory and file data
 */
function updateAssetTreeData(
  assetTree: IAssetTree,
  directories: FileItem[],
  files: FileItem[],
  recursive: boolean,
  scope?: string,
  initial: boolean = false
) {
  if (DEBUG_LOG.updateAssetTreeData) console.log('📦 updateAssetTreeData:', {
    scope,
    recursive,
    initial,
    directoriesCount: directories.length,
    filesCount: files.length,
    directoryPaths: directories.map(d => d.path),
    filePaths: files.map(f => f.path)
  });
  // Update existing folders and add new ones
  directories.forEach(folder => {
    const existingFolder = assetTree.findFolder(folder.path);
    if (existingFolder) {
      existingFolder.update(folder);
    } else {
      const isProjectAssetsFolder = folder.path === PROJECT_PATH_PREFIX + '/assets';
      const newFolder = AssetFile.create({
        path: folder.path,
        name: folder.name,
        isFolder: true,
        isExpanded: isProjectAssetsFolder // Expand project assets folder by default
      });
      newFolder.update(folder);
      assetTree.folders.put(newFolder);
      // Check if this matches preselected path
      if (folder.path === assetTree.preSelectedPath) {
        assetTree.setSelectedItem(newFolder);
      }
    }
  });

  // Update existing files and add new ones
  files.forEach(file => {
    const existingFile = assetTree.files.get(file.path);
    if (existingFile) {
      const didUpdate = existingFile.update(file);
      if (!didUpdate) {
        return;
      }

      // Load resource class for resource files
      if (existingFile.assetType === AssetType.Resource && !existingFile.resourceClass) {
        existingFile.loadResourceClass();
      }

      // Load prefab root class for prefab files
      if (existingFile.assetType === AssetType.Prefab && !existingFile.prefabRootClass) {
        existingFile.loadPrefabRootClass();
      }

      // Queue thumbnail generation for supported assets (service will check if needed)
      if (existingFile.supportsThumbnails && !initial) {
        const thumbnailService = getEditorStore().thumbnailService;
        if (thumbnailService) {
          thumbnailService.queueThumbnailGeneration(existingFile, 1);
        }
      }
    } else {
      const newFile = AssetFile.create({ path: file.path, name: file.name, isFolder: false });
      newFile.update(file);
      assetTree.files.put(newFile);

      // Load resource class for resource files
      if (newFile.assetType === AssetType.Resource) {
        newFile.loadResourceClass();
      }

      // Load prefab root class for prefab files
      if (newFile.assetType === AssetType.Prefab) {
        newFile.loadPrefabRootClass();
      }

      // Queue thumbnail generation for new supported assets
      if (newFile.supportsThumbnails && !initial) {
        const thumbnailService = getEditorStore().thumbnailService;
        if (thumbnailService) {
          thumbnailService.queueThumbnailGeneration(newFile, 0);
        }
      }

      // Check if this matches preselected path
      if (file.path === assetTree.preSelectedPath) {
        assetTree.setSelectedItem(newFile);
      }
    }
  });

  // Remove items that no longer exist within the scope
  const foldersToRemove: string[] = [];
  const filesToRemove: string[] = [];
  const incomingFolderPaths = new Set(directories.map(folder => folder.path));
  const incomingFilePaths = new Set(files.map(file => file.path));

  // Get all items that should be considered for removal based on scope
  let itemsInScope: IAssetFile[];
  if (!scope) {
    // If no scope, consider all items
    itemsInScope = assetTree.getItems();
  } else {
    // For scoped refresh, we need to check items differently:
    // - For recursive: all items that are within the scope path (including subdirectories)
    // - For non-recursive: all direct children of the scope path
    itemsInScope = assetTree.getItems().filter(item => {
      if (recursive) {
        // For recursive, item must be within the scope directory
        // Either it's the scope itself, or it starts with scope + '/'
        return item.path === scope || item.path.startsWith(scope + '/');
      } else {
        return pathlib.dirname(item.path) === scope;
      }
    });
  }

  if (DEBUG_LOG.updateAssetTreeData) console.log('📦 Items in scope check:', {
    scope,
    recursive,
    itemsInScopeCount: itemsInScope.length,
    itemsInScopePaths: itemsInScope.map(item => ({ path: item.path, isFolder: item.isFolder })),
    allFoldersCount: assetTree.folders.size,
    allFilesCount: assetTree.files.size,
    incomingDirectoriesCount: directories.length,
    incomingFilesCount: files.length
  });

  itemsInScope.forEach(item => {
    const path = item.path;

    // Skip custom/virtual folders - they should never be removed during directory refresh
    // Only skip actual virtual/custom paths (not regular files/folders under @project or @engine)
    if (item.isFolder && (item.path.startsWith('#') ||
      item.path === PROJECT_PATH_PREFIX || item.path === ENGINE_PATH_PREFIX)) {
      if (DEBUG_LOG.updateAssetTreeData) console.log('📦 Skipping virtual/custom folder:', { path });
      return;
    }

    // Skip the scope folder itself - when refreshing a directory, the directory itself
    // is not included in the results, only its contents are
    if (scope && item.path === scope) {
      if (DEBUG_LOG.updateAssetTreeData) console.log('📦 Skipping scope folder itself:', { path, scope });
      return;
    }

    if (item.isFolder) {
      const folderExists = incomingFolderPaths.has(path);
      if (!folderExists) {
        foldersToRemove.push(item.path);
        if (DEBUG_LOG.updateAssetTreeData) console.log('📦 Folder marked for removal:', { path, reason: 'not found in directories', scope, recursive });
      }
    } else {
      const fileExists = incomingFilePaths.has(path);
      if (!fileExists) {
        filesToRemove.push(path);
        if (DEBUG_LOG.updateAssetTreeData) console.log('📦 File marked for removal:', { path, reason: 'not found in files', scope, recursive });
      }
    }
  });

  if (DEBUG_LOG.updateAssetTreeData) console.log('📦 Items to remove:', {
    foldersToRemove: foldersToRemove.length,
    filesToRemove: filesToRemove.length,
    foldersToRemoveDetail: foldersToRemove,
    filesToRemoveDetail: filesToRemove
  });

  foldersToRemove.forEach(path => assetTree.folders.delete(path));
  filesToRemove.forEach(path => assetTree.files.delete(path));

  // Handle thumbnail cache invalidation and cleanup for changed assets
  const thumbnailService = getEditorStore().thumbnailService;
  if (thumbnailService) {
    // Invalidate cache for deleted/changed assets
    const deletedPaths = [...filesToRemove, ...foldersToRemove];
    if (deletedPaths.length > 0) {
      thumbnailService.invalidateThumbnailCache(deletedPaths);

      // Clean up thumbnails for the specific deleted files and folders
      setTimeout(() => {
        thumbnailService.cleanupDeletedThumbnails(deletedPaths);
      }, 500); // Small delay to avoid blocking the asset tree update
    }

    // Also invalidate cache for newly added files (they might have changed)
    const newFilePaths = files.map(file => file.path);
    if (newFilePaths.length > 0) {
      thumbnailService.invalidateThumbnailCache(newFilePaths);
    }
  }

  if (DEBUG_LOG.assetsToRemove) console.log('[updateAssetTreeData] foldersToRemove:', foldersToRemove, 'filesToRemove:', filesToRemove);
};


/**
 * Transforms flat folder structure into hierarchical tree
 */
const directoryTreeTransformer = createTransformer((assetTree: IAssetTree): IDirectoryTreeNode[] => {
  const { folders, customFolders } = assetTree;
  const toNode = (folder: IAssetFile): IDirectoryTreeNode => ({
    id: folder.id,
    path: folder.path,
    name: folder.name,
    children: [],
  });

  // Create root nodes for project and engine.
  const projectRoot = toNode(customFolders.get(PROJECT_PATH_PREFIX)!);
  const engineRoot = toNode(customFolders.get(ENGINE_PATH_PREFIX)!);
  const rootFolders: IDirectoryTreeNode[] = [projectRoot, engineRoot];

  // Map to store all nodes by path, skipping folders that are hidden or nested within hidden directories
  const folderMap = new Map<string, IDirectoryTreeNode>();
  const isHiddenPath = (path: string) => path.split('/').some(part => part.startsWith('.'));
  for (const folder of folders.values()) {
    if (!isHiddenPath(folder.path)) folderMap.set(folder.path, toNode(folder));
  }

  // Attach folders to their correct root and parent, skipping hidden folders
  for (const folder of folders.values()) {
    if (isHiddenPath(folder.path)) continue;
    const node = folderMap.get(folder.path)!;
    if (folder.path.startsWith(PROJECT_PATH_PREFIX + '/')) {
      const relativePath = folder.path.slice(PROJECT_PATH_PREFIX.length + 1);
      const pathParts = relativePath.split('/').filter(Boolean);
      if (pathParts.length === 1) projectRoot.children!.push(node);
      else {
        const parentPath = PROJECT_PATH_PREFIX + '/' + pathParts.slice(0, -1).join('/');
        const parent = folderMap.get(parentPath);
        parent ? parent.children!.push(node) : projectRoot.children!.push(node);
      }
    } else if (folder.path.startsWith(ENGINE_PATH_PREFIX + '/')) {
      const relativePath = folder.path.slice(ENGINE_PATH_PREFIX.length + 1);
      const pathParts = relativePath.split('/').filter(Boolean);
      if (pathParts.length === 1) engineRoot.children!.push(node);
      else {
        const parentPath = ENGINE_PATH_PREFIX + '/' + pathParts.slice(0, -1).join('/');
        const parent = folderMap.get(parentPath);
        parent ? parent.children!.push(node) : engineRoot.children!.push(node);
      }
    } else {
      // Fallback: treat as top-level if not under known roots.
      rootFolders.push(node);
    }
  }

  return rootFolders;
});